import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { format, subDays, startOfMonth } from 'date-fns';
import { 
  Search, 
  Edit2, 
  Trash2, 
  CheckCircle2,
  XCircle,
  Calendar,
  Building2,
  User,
  Users,
  ShieldAlert,
  AlertTriangle,
  Info,
  Download,
  Filter,
  FileSpreadsheet,
  FileText,
  Plus,
  Calculator
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReceiptsListPage = () => {
  const { t, isRTL } = useLanguage();
  const { isAdmin } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [filteredReceipts, setFilteredReceipts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [doseRules, setDoseRules] = useState([]);
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [editFamilyMembers, setEditFamilyMembers] = useState([]);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Export dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    dateRange: 'all',
    startDate: '',
    endDate: '',
    status: 'all'
  });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchReceipts();
    fetchLocations();
    fetchDoseRules();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = receipts.filter(r => 
        r.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.patient_id_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredReceipts(filtered);
    } else {
      setFilteredReceipts(receipts);
    }
  }, [searchTerm, receipts]);

  const fetchReceipts = async () => {
    try {
      const response = await axios.get(`${API}/receipts`);
      setReceipts(response.data);
      setFilteredReceipts(response.data);
    } catch (error) {
      console.error('Error fetching receipts:', error);
      toast.error(isRTL ? 'خطأ في جلب البيانات' : 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchDoseRules = async () => {
    try {
      const response = await axios.get(`${API}/dose-rules`);
      setDoseRules(response.data);
    } catch (error) {
      console.error('Error fetching dose rules:', error);
    }
  };

  const calculateTablets = (age) => {
    if (!age || age <= 0 || doseRules.length === 0) return 0;
    const rule = doseRules.find(r => age >= r.min_age && age <= r.max_age);
    return rule ? rule.tablets : 0;
  };

  const getLocationName = (locationId) => {
    const loc = locations.find(l => l.id === locationId);
    return loc?.name || '-';
  };

  const handleEdit = (receipt) => {
    setEditingReceipt({ ...receipt });
    setEditFamilyMembers(receipt.family_members ? [...receipt.family_members] : []);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    try {
      const payload = {
        patient_name: editingReceipt.patient_name,
        patient_id_number: editingReceipt.patient_id_number,
        patient_age: parseInt(editingReceipt.patient_age) || 0,
        receipt_date: editingReceipt.receipt_date,
        location_id: editingReceipt.location_id,
        receipt_status: editingReceipt.receipt_status,
        dose_mg: parseFloat(editingReceipt.dose_mg) || 0,
        has_allergy: editingReceipt.has_allergy || false,
        family_members: editFamilyMembers.map(m => ({
          ...m,
          age: parseInt(m.age) || 0,
          dose_mg: parseFloat(m.dose_mg) || 0,
          tablets: parseInt(m.tablets) || 0
        }))
      };
      await axios.put(`${API}/receipts/${editingReceipt.id}`, payload);
      toast.success(t('receipt_updated'));
      setEditDialogOpen(false);
      fetchReceipts();
    } catch (error) {
      console.error('Error updating receipt:', error);
      const errorMsg = error.response?.data?.detail || (isRTL ? 'خطأ في التحديث' : 'Error updating');
      toast.error(errorMsg);
    }
  };

  const addEditFamilyMember = () => {
    setEditFamilyMembers(prev => [...prev, { name: '', id_number: '', age: 0, has_allergy: false, allergy_notes: '', dose_mg: 0, tablets: 0 }]);
  };

  const removeEditFamilyMember = (index) => {
    setEditFamilyMembers(prev => prev.filter((_, i) => i !== index));
  };

  const updateEditFamilyMember = (index, field, value) => {
    setEditFamilyMembers(prev => prev.map((m, i) => {
      if (i === index) {
        const updated = { ...m, [field]: value };
        if (field === 'has_allergy' && value === true) {
          updated.dose_mg = 0;
          updated.tablets = 0;
        }
        if (field === 'age' && value && !updated.has_allergy) {
          const tablets = calculateTablets(parseInt(value));
          updated.dose_mg = tablets;
          updated.tablets = tablets;
        }
        return updated;
      }
      return m;
    }));
  };

  const handleDelete = (id) => {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/receipts/${deletingId}`);
      toast.success(t('receipt_deleted'));
      setDeleteDialogOpen(false);
      fetchReceipts();
    } catch (error) {
      console.error('Error deleting receipt:', error);
      toast.error(isRTL ? 'خطأ في الحذف' : 'Error deleting');
    }
  };

  const handleExport = async (type, filters = {}) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      
      if (filters.dateRange === 'today') {
        const today = format(new Date(), 'yyyy-MM-dd');
        params.append('start_date', today);
        params.append('end_date', today);
      } else if (filters.dateRange === 'week') {
        const weekAgo = format(subDays(new Date(), 7), 'yyyy-MM-dd');
        const today = format(new Date(), 'yyyy-MM-dd');
        params.append('start_date', weekAgo);
        params.append('end_date', today);
      } else if (filters.dateRange === 'month') {
        const monthStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
        const today = format(new Date(), 'yyyy-MM-dd');
        params.append('start_date', monthStart);
        params.append('end_date', today);
      } else if (filters.dateRange === 'custom' && filters.startDate && filters.endDate) {
        params.append('start_date', filters.startDate);
        params.append('end_date', filters.endDate);
      }
      
      if (filters.status && filters.status !== 'all') {
        params.append('receipt_status', filters.status);
      }
      
      const response = await axios.get(`${API}/export/${type}?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const today = format(new Date(), 'yyyy-MM-dd');
      const filename = `سجلات_استلام_الدواء_${today}.${type === 'excel' ? 'xlsx' : 'pdf'}`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(isRTL ? 'تم التحميل بنجاح' : 'Download successful');
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error(isRTL ? 'خطأ في التحميل' : 'Error downloading');
    } finally {
      setExporting(false);
    }
  };

  const handleQuickExport = () => {
    handleExport('excel', { dateRange: 'all', status: 'all' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 border-4 border-sky-500/30 border-t-sky-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="receipts-list-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {t('receipts_title')}
          </h1>
          <p className="text-slate-500">
            {isRTL ? `${filteredReceipts.length} سجل` : `${filteredReceipts.length} records`}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button onClick={handleQuickExport} className="gap-2 bg-emerald-500 hover:bg-emerald-600" disabled={exporting}>
            <Download className="h-4 w-4" />
            {isRTL ? 'تحميل Excel' : 'Download Excel'}
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => setExportDialogOpen(true)}>
            <Filter className="h-4 w-4" />
            {isRTL ? 'تحميل مخصص' : 'Custom Export'}
          </Button>
        </div>
      </div>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-emerald-500" />
              {isRTL ? 'تحميل ملف Excel' : 'Download Excel File'}
            </DialogTitle>
            <DialogDescription>
              {isRTL ? 'اختر خيارات التصفية للملف' : 'Choose filter options for the file'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-medium">{isRTL ? 'الفترة الزمنية' : 'Date Range'}</Label>
              <Select value={exportFilters.dateRange} onValueChange={(value) => setExportFilters({...exportFilters, dateRange: value})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'جميع السجلات' : 'All Records'}</SelectItem>
                  <SelectItem value="today">{isRTL ? 'اليوم' : 'Today'}</SelectItem>
                  <SelectItem value="week">{isRTL ? 'آخر أسبوع' : 'Last Week'}</SelectItem>
                  <SelectItem value="month">{isRTL ? 'هذا الشهر' : 'This Month'}</SelectItem>
                  <SelectItem value="custom">{isRTL ? 'فترة مخصصة' : 'Custom Range'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {exportFilters.dateRange === 'custom' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm">{isRTL ? 'من تاريخ' : 'From Date'}</Label>
                  <Input type="date" value={exportFilters.startDate} onChange={(e) => setExportFilters({...exportFilters, startDate: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">{isRTL ? 'إلى تاريخ' : 'To Date'}</Label>
                  <Input type="date" value={exportFilters.endDate} onChange={(e) => setExportFilters({...exportFilters, endDate: e.target.value})} />
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="font-medium">{isRTL ? 'حالة الاستلام' : 'Receipt Status'}</Label>
              <Select value={exportFilters.status} onValueChange={(value) => setExportFilters({...exportFilters, status: value})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'جميع الحالات' : 'All Statuses'}</SelectItem>
                  <SelectItem value="received">{isRTL ? 'تم الاستلام' : 'Received'}</SelectItem>
                  <SelectItem value="not_received">{isRTL ? 'لم يستلم' : 'Not Received'}</SelectItem>
                  <SelectItem value="preparing">{isRTL ? 'قيد التحضير' : 'Preparing'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setExportDialogOpen(false)}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
            <Button onClick={() => handleExport('excel', exportFilters)} className="bg-emerald-500 hover:bg-emerald-600 gap-2" disabled={exporting}>
              {exporting ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Download className="h-4 w-4" />}
              {isRTL ? 'تحميل' : 'Download'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="animate-fade-in stagger-1">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="text-lg">{t('receipts_list')}</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 ${isRTL ? 'right-3' : 'left-3'}`} />
              <Input
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`h-10 ${isRTL ? 'pr-10' : 'pl-10'}`}
                data-testid="search-input"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-4 mt-4 p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <Info className="h-4 w-4 text-slate-400" />
              <span className="text-slate-600 font-medium">{isRTL ? 'دليل الألوان:' : 'Status Legend:'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
              <span className="text-sm text-slate-600">{t('received')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              <span className="text-sm text-slate-600">{t('not_received')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-amber-500"></span>
              <span className="text-sm text-slate-600">{t('preparing')}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredReceipts.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>{t('no_data')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('patient_name')}</TableHead>
                    <TableHead>{t('patient_id_number')}</TableHead>
                    <TableHead>{t('patient_age')}</TableHead>
                    <TableHead>{t('family_count')}</TableHead>
                    <TableHead>{t('receipt_date')}</TableHead>
                    <TableHead>{t('receiving_area')}</TableHead>
                    <TableHead>{t('dose_mg')}</TableHead>
                    <TableHead>{t('receipt_status')}</TableHead>
                    <TableHead>{t('has_allergy')}</TableHead>
                    <TableHead className="text-center">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReceipts.map((receipt, index) => {
                    const familyMembers = receipt.family_members || [];
                    const familyCount = familyMembers.length;
                    const familyDoseTotal = familyMembers.reduce((sum, m) => sum + (m.dose_mg || 0), 0);
                    const totalDose = (receipt.dose_mg || 0) + familyDoseTotal;
                    
                    return (
                    <TableRow 
                      key={receipt.id}
                      className="animate-fade-in"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="h-4 w-4 text-slate-500" />
                          </div>
                          {receipt.patient_name}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 font-mono">
                        {receipt.patient_id_number || '-'}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {receipt.patient_age || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {familyCount > 0 ? (
                            <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">
                              <Users className="h-3 w-3 me-1" />
                              {familyCount}
                            </Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Calendar className="h-4 w-4" />
                          {receipt.receipt_date}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Building2 className="h-4 w-4" />
                          {getLocationName(receipt.location_id)}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        <div className="flex flex-col">
                          <span className="font-medium">{totalDose} {isRTL ? 'حبة' : 'Tablet'}</span>
                          {familyCount > 0 && (
                            <span className="text-xs text-slate-400">
                              ({isRTL ? 'المريض' : 'Patient'}: {receipt.dose_mg || 0}, {isRTL ? 'العائلة' : 'Family'}: {familyDoseTotal})
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          className={
                            receipt.receipt_status === 'received' 
                              ? 'bg-emerald-500 hover:bg-emerald-600' 
                              : receipt.receipt_status === 'preparing'
                              ? 'bg-amber-500 hover:bg-amber-600'
                              : 'bg-red-500 hover:bg-red-600'
                          }
                          data-testid={`status-badge-${receipt.id}`}
                        >
                          {receipt.receipt_status === 'received' 
                            ? <><CheckCircle2 className="h-3 w-3 me-1" />{t('received')}</>
                            : receipt.receipt_status === 'preparing'
                            ? <><AlertTriangle className="h-3 w-3 me-1" />{t('preparing')}</>
                            : <><XCircle className="h-3 w-3 me-1" />{t('not_received')}</>
                          }
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {receipt.has_allergy ? (
                          <Badge className="bg-amber-500 hover:bg-amber-600">
                            <ShieldAlert className="h-3 w-3 me-1" />
                            {isRTL ? 'حساسية' : 'Allergy'}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(receipt)} className="h-8 w-8 p-0" data-testid={`edit-button-${receipt.id}`}>
                            <Edit2 className="h-4 w-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(receipt.id)} className="h-8 w-8 p-0 hover:text-red-600" data-testid={`delete-button-${receipt.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Edit Dialog with Family Members */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('edit')}</DialogTitle>
            <DialogDescription>
              {isRTL ? 'تعديل بيانات المريض' : 'Edit patient data'}
            </DialogDescription>
          </DialogHeader>
          
          {editingReceipt && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('patient_name')}</Label>
                  <Input
                    value={editingReceipt.patient_name}
                    onChange={(e) => setEditingReceipt({...editingReceipt, patient_name: e.target.value})}
                    data-testid="edit-patient-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('patient_id_number')}</Label>
                  <Input
                    value={editingReceipt.patient_id_number || ''}
                    onChange={(e) => setEditingReceipt({...editingReceipt, patient_id_number: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('patient_age')}</Label>
                  <Input
                    type="number"
                    min="0"
                    max="150"
                    value={editingReceipt.patient_age || ''}
                    onChange={(e) => {
                      const age = parseInt(e.target.value) || 0;
                      const tablets = calculateTablets(age);
                      setEditingReceipt({...editingReceipt, patient_age: age, dose_mg: editingReceipt.has_allergy ? 0 : tablets});
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('receipt_date')}</Label>
                  <Input
                    type="date"
                    value={editingReceipt.receipt_date}
                    onChange={(e) => setEditingReceipt({...editingReceipt, receipt_date: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('receiving_area')}</Label>
                  <Select
                    value={editingReceipt.location_id || ''}
                    onValueChange={(value) => setEditingReceipt({...editingReceipt, location_id: value})}
                  >
                    <SelectTrigger><SelectValue placeholder={isRTL ? 'اختر' : 'Select'} /></SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('dose_mg')}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editingReceipt.dose_mg || ''}
                    onChange={(e) => setEditingReceipt({...editingReceipt, dose_mg: parseFloat(e.target.value) || 0})}
                    disabled={editingReceipt.has_allergy}
                  />
                  <p className="text-xs text-amber-600">
                    {isRTL ? '⚠️ تعديل الجرعة سيؤثر على المخزون تلقائياً' : '⚠️ Changing dose will automatically adjust inventory'}
                  </p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{t('receipt_status')}</Label>
                <RadioGroup
                  value={editingReceipt.receipt_status}
                  onValueChange={(value) => {
                    if (editingReceipt.has_allergy && value === 'received') {
                      toast.error(isRTL ? 'لا يمكن تغيير الحالة للحساسية' : 'Cannot change status for allergy');
                      return;
                    }
                    setEditingReceipt({...editingReceipt, receipt_status: value});
                  }}
                  className="flex flex-col gap-2"
                >
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <RadioGroupItem value="received" id="edit-received" disabled={editingReceipt.has_allergy} />
                    <Label htmlFor="edit-received" className="text-emerald-600 cursor-pointer flex items-center gap-1">
                      <CheckCircle2 className="h-4 w-4" />{t('received')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <RadioGroupItem value="not_received" id="edit-not-received" />
                    <Label htmlFor="edit-not-received" className="text-red-600 cursor-pointer flex items-center gap-1">
                      <XCircle className="h-4 w-4" />{t('not_received')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <RadioGroupItem value="preparing" id="edit-preparing" />
                    <Label htmlFor="edit-preparing" className="text-amber-600 cursor-pointer flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4" />{t('preparing')}
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Family Members Edit */}
              {editingReceipt.receipt_type === 'family' && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <Users className="h-4 w-4 text-sky-500" />
                      {t('family_members')}
                    </Label>
                    <Button type="button" variant="outline" size="sm" onClick={addEditFamilyMember} className="gap-1">
                      <Plus className="h-3 w-3" />{t('add_family_member')}
                    </Button>
                  </div>
                  {editFamilyMembers.map((member, idx) => (
                    <div key={idx} className="p-3 mb-2 rounded-lg bg-slate-50 border space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{isRTL ? `فرد ${idx + 1}` : `Member ${idx + 1}`}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeEditFamilyMember(idx)} className="text-red-500 h-6 w-6 p-0">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <Input placeholder={t('patient_name')} value={member.name || ''} onChange={(e) => updateEditFamilyMember(idx, 'name', e.target.value)} className="h-9 text-sm" />
                        <Input placeholder={t('patient_id_number')} value={member.id_number || ''} onChange={(e) => updateEditFamilyMember(idx, 'id_number', e.target.value)} className="h-9 text-sm" />
                        <Input type="number" placeholder={t('age')} value={member.age || ''} onChange={(e) => updateEditFamilyMember(idx, 'age', e.target.value)} className="h-9 text-sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input type="number" placeholder={t('dose_mg')} value={member.dose_mg || ''} onChange={(e) => updateEditFamilyMember(idx, 'dose_mg', e.target.value)} disabled={member.has_allergy} className="h-9 text-sm" />
                        <div className="flex items-center gap-2">
                          <Checkbox checked={member.has_allergy || false} onCheckedChange={(checked) => updateEditFamilyMember(idx, 'has_allergy', checked)} />
                          <Label className="text-xs">{t('has_allergy_yes')}</Label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>{t('cancel')}</Button>
            <Button onClick={handleEditSave} className="bg-sky-500 hover:bg-sky-600" data-testid="edit-save-button">{t('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">{t('delete')}</DialogTitle>
            <DialogDescription>{t('confirm_delete')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>{t('cancel')}</Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="confirm-delete-button">{t('delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceiptsListPage;
